<template>
  <AuthLayout :mode="mode">
    <Section heading="Manage Ethics Records" :mode="mode" />
    <AppCard :mode="mode">
      <template #body>
        <div class="grid gap-4">
          <div class="grid grid-cols-4 gap-3 sm:grid-cols-1">
            <AppSelect
              :mode="mode"
              title="Category"
              v-model="filterCategory"
              @change="fetch"
            >
              <template #options>
                <option value="">All</option>
                <option value="violator">Violator</option>
                <option value="non_participant">Non-Participant</option>
                <option value="fraud_alert">Fraud Alert</option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              id="admin-ethics-search"
              title="Search"
              placeholder="Search..."
              v-model="q"
              @input="onSearch"
            />
            <div class="flex items-end">
              <AppButton title="Add new" @action="openCreate" />
            </div>
          </div>

          <table class="w-full text-left text-[14px]">
            <thead>
              <tr
                :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-80']"
              >
                <th class="p-2">Category</th>
                <th class="p-2">Respondent</th>
                <th class="p-2">Complainant</th>
                <th class="p-2">Date</th>
                <th class="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in records" :key="r.ethics_record_id" class="border-t">
                <td class="p-2">{{ categoryLabel(r.category) }}</td>
                <td class="p-2">{{ r.respondent && r.respondent.company }}</td>
                <td class="p-2">{{ r.complainant && r.complainant.company }}</td>
                <td class="p-2">{{ r.date_display }}</td>
                <td class="p-2">
                  <button
                    class="mr-3 text-primary-100 underline"
                    @click="openEdit(r)"
                  >
                    Edit
                  </button>
                  <button
                    class="text-red-600 underline"
                    @click="confirmDelete(r)"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              <tr v-if="records.length === 0">
                <td colspan="5" class="p-4 text-center text-black-60">
                  No records.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </AppCard>

    <div
      v-if="editing"
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
      @click.self="closeEdit"
    >
      <div
        class="my-8 w-full max-w-3xl rounded-xl p-6"
        :class="[mode === 'dark' ? 'bg-dark-sidebar' : 'bg-white-100']"
      >
        <h3 class="mb-4 text-[22px] font-semibold">
          {{ editing.ethics_record_id ? "Edit" : "Add" }} Ethics Record
        </h3>

        <div class="grid grid-cols-2 gap-4 sm:grid-cols-1">
          <AppSelect :mode="mode" title="Category" v-model="form.category">
            <template #options>
              <option value="violator">Violator</option>
              <option value="non_participant">Non-Participant</option>
              <option value="fraud_alert">Fraud Alert</option>
            </template>
          </AppSelect>
          <AppInput
            :mode="mode"
            id="form-date"
            title="Date (YYYY-MM-DD)"
            v-model="form.date"
            placeholder="2026-05-17"
          />
          <AppInput
            :mode="mode"
            id="form-date-display"
            title="Date display (free text)"
            v-model="form.date_display"
            placeholder="May 17, 2026"
          />
        </div>

        <fieldset class="mt-4 border p-3">
          <legend class="px-2 font-semibold">Respondent</legend>
          <PartyForm v-model="form.respondent" :mode="mode" />
        </fieldset>

        <fieldset class="mt-4 border p-3">
          <legend class="px-2 font-semibold">Complainant</legend>
          <PartyForm v-model="form.complainant" :mode="mode" />
        </fieldset>

        <div class="mt-4">
          <label class="mb-1 block font-semibold">Note (admin only)</label>
          <textarea
            v-model="form.note"
            rows="3"
            class="w-full rounded border p-2 text-[14px]"
          />
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button class="rounded border px-4 py-2" @click="closeEdit">
            Cancel
          </button>
          <AppButton title="Save" @action="save" />
        </div>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import ApiRequest from "@L/ApiRequest";
import { useToastStore } from "@S/toast";
import Section from "@C/Common/Section.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppButton from "@C/Common/AppButton.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AuthLayout from "@A/AuthLayout.vue";
import PartyForm from "@C/Ethics/PartyForm.vue";

const blankParty = () => ({
  company: "",
  contact_name: "",
  email: "",
  phone: "",
  details: [],
});

const blankRecord = () => ({
  ethics_record_id: null,
  category: "violator",
  date: "",
  date_display: "",
  note: "",
  respondent: blankParty(),
  complainant: blankParty(),
});

export default {
  name: "AdminEthicsRecords",
  components: {
    AuthLayout,
    Section,
    AppCard,
    AppButton,
    AppInput,
    AppSelect,
    PartyForm,
  },
  setup() {
    return { toastStore: useToastStore() };
  },
  props: { mode: String },
  data() {
    return {
      records: [],
      filterCategory: "",
      q: "",
      debounce: null,
      editing: null,
      form: blankRecord(),
    };
  },
  mounted() {
    this.fetch();
  },
  methods: {
    categoryLabel(c) {
      if (c === "violator") return "Violator";
      if (c === "non_participant") return "Non-Participant";
      if (c === "fraud_alert") return "Fraud Alert";
      return c;
    },
    async fetch() {
      const params = new URLSearchParams();
      if (this.filterCategory) params.set("category", this.filterCategory);
      if (this.q) params.set("q", this.q);
      try {
        const { data } = await ApiRequest().get(
          `/admin/ethics_records?${params.toString()}`,
        );
        this.records = (data || []).map((d) => d.ethics_record);
      } catch (e) {
        this.toastStore.show("Failed to load ethics records", "error");
        this.records = [];
      }
    },
    onSearch() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.fetch(), 300);
    },
    openCreate() {
      this.form = blankRecord();
      this.editing = this.form;
    },
    openEdit(r) {
      this.form = {
        ethics_record_id: r.ethics_record_id,
        category: r.category,
        date: r.date || "",
        date_display: r.date_display || "",
        note: r.note || "",
        respondent: { ...blankParty(), ...(r.respondent || {}) },
        complainant: { ...blankParty(), ...(r.complainant || {}) },
      };
      this.editing = this.form;
    },
    closeEdit() {
      this.editing = null;
    },
    async save() {
      const body = {
        category: this.form.category,
        date: this.form.date || null,
        date_display: this.form.date_display,
        note: this.form.note,
        respondent: this.form.respondent,
        complainant: this.form.complainant,
      };
      try {
        if (this.form.ethics_record_id) {
          await ApiRequest().put(
            `/admin/ethics_records/${this.form.ethics_record_id}`,
            body,
          );
          this.toastStore.show("Saved", "info");
        } else {
          await ApiRequest().post(`/admin/ethics_records`, body);
          this.toastStore.show("Created", "info");
        }
        this.closeEdit();
        this.fetch();
      } catch (e) {
        this.toastStore.show("Save failed", "error");
      }
    },
    async confirmDelete(r) {
      if (!confirm(`Delete the ethics record for "${r.respondent && r.respondent.company}"?`)) return;
      try {
        await ApiRequest().delete(`/admin/ethics_records/${r.ethics_record_id}`);
        this.toastStore.show("Deleted", "info");
        this.fetch();
      } catch (e) {
        this.toastStore.show("Delete failed", "error");
      }
    },
  },
};
</script>
